import React from "react";
import type { ChangesWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/changesWorkflow";
import WorkflowReview from "./WorkflowReview";
import { Heading, CodeDisplay, Alert } from "@aidkitorg/component-library";

const ansiToReact = (input: string) => {
  const segments: React.ReactNode[] = [];
  let i = 0;
  let currentColor: string | undefined = undefined;
  const colorMap: Record<string, string | undefined> = {
    '\x1b[31m': 'red',
    '\x1b[32m': 'green',
    '\x1b[39m': undefined,
  };

  const regex = /\x1b\[\d{2}m/g;
  let match: RegExpExecArray | null;
  const matches: RegExpExecArray[] = [];
  
  while ((match = regex.exec(input)) !== null) {
    matches.push(match);
  }

  for (const match of matches) {
    const start = match.index;
    const token = match[0];

    if (start > i) {
      const text = input.slice(i, start);
      segments.push(
        <pre 
          style={{ 
            color: currentColor, 
            margin: 0,
            whiteSpace: 'pre',
            display: 'inline'
          }} 
          key={i}
        >
          {text}
        </pre>
        
      );
    }

    currentColor = colorMap[token];
    i = start + token.length;
  }

  // Push the last remaining segment
  if (i < input.length) {
    const text = input.slice(i);
    segments.push(
      <pre 
        style={{ 
          color: currentColor, 
          margin: 0,
          whiteSpace: 'pre',
          display: 'inline'
        }} 
        key={i}
      >
        {text}
      </pre>
    );
  }

  return segments;
};

export function ChangesWorkflowReview({ distroCommitId, surveyHashMismatch, isPendingReview, isApproved }: { distroCommitId: string, surveyHashMismatch: boolean, isPendingReview: boolean, isApproved: boolean }) {
  const renderChangesResults = (workflow: any) => {
    if (!workflow?.result?.success) {
      return null;
    }

    const result = workflow.result as ChangesWorkflowResult;

    return (
      <div className="space-y-6">
        {surveyHashMismatch && (isPendingReview || isApproved) && (
          <div className="mb-6">
            <p className="text-red-500 font-bold">
              The survey has been saved outside of the review flow. 
              This diff no longer reflects the current state of the survey.
            </p>
          </div>
        )}
        {result.diff ? (
          <div className="mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm whitespace-pre font-mono p-2">
              {ansiToReact(result.diff).map((segment, index) => (
                <React.Fragment key={index}>{segment}</React.Fragment>
              ))}
            </div>
          </div>
        ) : (
          <Alert
            title="No Diff Available"
            variant="warning"
            className="mb-6"
          >
            No diff available. The proposed survey is identical to the current survey.
          </Alert>
        )}

      </div>
    );
  };

  return (
    <WorkflowReview
      distroCommitId={distroCommitId}
      workflowName="changes"
      title="Survey Changes"
      renderSummary={renderChangesResults}
    />
  );
}
